<!--
  Ported from vendor-ref/mara/app/ui/src/components/nanome/WorkspaceSnapshotPopup.vue. Sizes come from
  SNAPSHOT.presets in @nanome-lite/look (ImageCaptureRenderer.cs:12-20); the engine returns a PNG Blob which
  file-saver writes as '<entry> - Nanome Lite.png'.
-->
<script setup lang="ts">
import { computed, ref } from 'vue';
import { saveAs } from 'file-saver';
import { useToast } from 'primevue/usetoast';
import Popover from 'primevue/popover';
import Select from 'primevue/select';
import ToggleSwitch from 'primevue/toggleswitch';
import Button from 'primevue/button';
import { SNAPSHOT } from '@nanome-lite/look';
import { useLiteStore } from '@nanome-lite/state';
import { useDevice } from '@/composables/device';

const store = useLiteStore();
const device = useDevice();
const toast = useToast();
const panel = ref<InstanceType<typeof Popover>>();

const DIMENSIONS = SNAPSHOT.presets.map(p => ({ label: `${p.label} (${p.width} × ${p.height})`, width: p.width, height: p.height }));
const dimensions = ref(DIMENSIONS[SNAPSHOT.defaultIndex] ?? DIMENSIONS[0]!);
const includeBackground = ref<boolean>(SNAPSHOT.includeBackgroundDefault);
const isCapturing = ref(false);

/** Mobile GPUs cap at SNAPSHOT.maxEdgeMobile; larger presets stay selectable but are clamped by the engine. */
const options = computed(() => DIMENSIONS.map(d => ({ ...d, label: device.isMobile.value && Math.max(d.width, d.height) > SNAPSHOT.maxEdgeMobile ? `${d.label} — clamped on mobile` : d.label })));

const filename = computed(() => {
  const entry = store.entries.map(e => e.name).join(', ') || 'Workspace';
  return `${entry} - Nanome Lite.png`;
});

async function capture() {
  if (!store.engine) { toast.add({ severity: 'error', summary: 'No renderer', detail: 'The viewer engine is not mounted', life: 3000 }); return; }
  isCapturing.value = true;
  try {
    const blob = await store.engine.snapshot({ width: dimensions.value.width, height: dimensions.value.height, includeBackground: includeBackground.value });
    saveAs(blob, filename.value);
    panel.value?.hide();
  }
  catch (error) {
    toast.add({ severity: 'error', summary: 'Snapshot failed', detail: error instanceof Error ? error.message : String(error), life: 3000 });
  }
  finally {
    isCapturing.value = false;
  }
}

defineExpose({ show: (e: Event) => panel.value?.show(e), hide: () => panel.value?.hide() });
</script>

<template>
  <Popover ref="panel" pt:content="flex flex-col gap-2 w-72">
    <h4 class="my-1">
      Capture Image
    </h4>
    <Select v-model="dimensions" :options="options" option-label="label" append-to="self" size="small" class="w-full" />
    <label class="my-1 flex items-center justify-between gap-2 cursor-pointer">
      Include background
      <ToggleSwitch v-model="includeBackground" />
    </label>
    <div class="text-xs op-50 truncate" :title="filename">
      {{ filename }}
    </div>
    <Button icon="i-tabler-camera" label="Capture" size="small" rounded :loading="isCapturing" @click="capture" />
  </Popover>
</template>
